import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut, Inbox } from "lucide-react";
import { LogoLockup } from "./Logo";
import { useAuth } from "../App";
import { useLang, LangSwitcher } from "../i18n";

const LINKS = [
  { href: "#chi-siamo", id: "nav-link-chi-siamo", it: "Chi siamo", en: "About" },
  { href: "#servizi", id: "nav-link-servizi", it: "Servizi", en: "Services" },
  { href: "#lavori", id: "nav-link-lavori", it: "Lavori", en: "Projects" },
  { href: "/guide", id: "nav-link-guide", route: true, it: "Guide", en: "Guides" },
  { href: "#contatti", id: "nav-link-contatti", it: "Contatti", en: "Contact" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) {
    window.location.href = "/" + href;
    return;
  }
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2.05 3.5-5.05 3.5-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.06 7.9-2.9l-3.75-2.9c-1 .7-2.35 1.2-4.15 1.2-3.15 0-5.8-2.1-6.75-5l-.14.01-3.1 2.4-.04.14C3.9 21.3 7.6 24 12 24z" />
    <path fill="#FBBC05" d="M5.25 14.4c-.25-.75-.4-1.55-.4-2.4s.15-1.65.42-2.4l-.01-.16-3.14-2.44-.1.05C1.4 8.6 1 10.2 1 12s.4 3.4 1.02 4.95l3.23-2.55z" />
    <path fill="#EA4335" d="M12 4.6c2.25 0 3.75.97 4.6 1.78l3.37-3.28C17.9 1.2 15.2 0 12 0 7.6 0 3.9 2.7 2.02 7.05l3.23 2.55C6.2 6.7 8.85 4.6 12 4.6z" />
  </svg>
);

export const loginWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const LoginButton = ({ testid, className = "" }) => {
  const { t } = useLang();
  return (
    <button
      data-testid={testid}
      onClick={loginWithGoogle}
      className={`inline-flex items-center gap-2 text-sm font-medium text-slate-200 border border-[#D4AF37]/40 px-5 py-2.5 rounded-full hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-colors duration-300 ${className}`}
    >
      <GoogleIcon />
      {t("Accedi", "Sign in")}
    </button>
  );
};

const UserMenu = ({ testid, compact = false }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  return (
    <span className="inline-flex items-center gap-3" data-testid={testid}>
      {user.is_admin && (
        <a
          href="/admin"
          data-testid={`${testid}-admin-link`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#F3E08B] border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 rounded-full px-3 py-2 transition-colors duration-300"
        >
          <Inbox size={13} />
          {t("Richieste", "Requests")}
        </a>
      )}
      {user.picture ? (
        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#D4AF37]/50" referrerPolicy="no-referrer" />
      ) : (
        <span className="w-8 h-8 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/15 flex items-center justify-center text-xs font-semibold text-[#F3E08B]">
          {(user.name || user.email || "?").charAt(0).toUpperCase()}
        </span>
      )}
      {!compact && (
        <span className="text-sm text-slate-200 max-w-[140px] truncate" data-testid={`${testid}-name`}>
          {user.name || user.email}
        </span>
      )}
      <button
        data-testid={`${testid}-logout-button`}
        onClick={logout}
        aria-label={t("Esci", "Sign out")}
        className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#F3E08B] border border-white/15 hover:border-[#D4AF37]/60 rounded-full px-3 py-2 transition-colors duration-300"
      >
        <LogOut size={13} />
        {t("Esci", "Sign out")}
      </button>
    </span>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  const linkCls = "text-sm tracking-wide text-slate-300 hover:text-[#F3E08B] transition-colors duration-300";

  const renderLink = (l) => {
    const label = t(l.it, l.en);
    return l.route ? (
      <a key={l.href} href={l.href} data-testid={l.id} className={linkCls}>
        {label}
      </a>
    ) : (
      <a key={l.href} href={l.href} data-testid={l.id} onClick={(e) => go(e, l.href)} className={linkCls}>
        {label}
      </a>
    );
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#070D18]/85 border-b border-[#D4AF37]/15" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a href="/" data-testid="nav-logo-link" aria-label="Adriatica - home">
          <LogoLockup compact />
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map(renderLink)}
          <a
            href="#contatti"
            data-testid="nav-cta-quote-button"
            onClick={(e) => go(e, "#contatti")}
            className="text-sm font-semibold tracking-wide text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-5 py-2.5 rounded-full hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow duration-300 whitespace-nowrap"
          >
            {t("Richiedi un preventivo", "Request a quote")}
          </a>
          <LangSwitcher />
          {user === null && <LoginButton testid="nav-login-button" />}
          {user && <UserMenu testid="nav-user-menu" />}
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <LangSwitcher compact />
          <button
            data-testid="nav-mobile-menu-button"
            className="text-slate-200 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("Chiudi menu", "Close menu") : t("Apri menu", "Open menu")}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden backdrop-blur-xl bg-[#070D18]/95 border-b border-[#D4AF37]/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => {
                const label = t(l.it, l.en);
                return l.route ? (
                  <a key={l.href} href={l.href} data-testid={`${l.id}-mobile`} className="text-base text-slate-200 hover:text-[#F3E08B] transition-colors">
                    {label}
                  </a>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    data-testid={`${l.id}-mobile`}
                    onClick={(e) => go(e, l.href)}
                    className="text-base text-slate-200 hover:text-[#F3E08B] transition-colors"
                  >
                    {label}
                  </a>
                );
              })}
              <a
                href="#contatti"
                data-testid="nav-cta-quote-button-mobile"
                onClick={(e) => go(e, "#contatti")}
                className="text-center text-sm font-semibold text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-5 py-3 rounded-full"
              >
                {t("Richiedi un preventivo", "Request a quote")}
              </a>
              {user === null && <LoginButton testid="nav-login-button-mobile" className="justify-center" />}
              {user && <UserMenu testid="nav-user-menu-mobile" compact />}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
